/**
 * Notification realtime-socket preference.
 *
 * Lets the user opt out of the Socket.IO connection used for realtime
 * notification pushes (NEW_NOTIFICATION). When disabled, the socket is
 * disconnected and no longer reconnects/polls in the background; the
 * notification list still works via the periodic REST load in
 * notificationsSlice, just without the realtime push.
 *
 * Cross-component reactivity mirrors searchShortcut.ts: persist to
 * localStorage and dispatch a custom DOM event so AuthContext (which owns
 * the actual connectSocket()/disconnectSocket() calls) can react without a
 * shared React context.
 */

const STORAGE_KEY = 'ggc_notification_socket_enabled';

/** Fired on `window` whenever the preference is changed. */
export const NOTIFICATION_SOCKET_PREF_CHANGE_EVENT = 'ggc:notification-socket-pref-change';

/** Default preserves current behavior — socket connects while logged in. */
const DEFAULT_ENABLED = true;

/** Read the persisted preference, falling back to the default (enabled). */
export function getStoredNotificationSocketEnabled(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch { /* ignore */ }
  return DEFAULT_ENABLED;
}

/** Persist the preference and notify all listeners via a custom event. */
export function setNotificationSocketEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch { /* ignore */ }
  window.dispatchEvent(
    new CustomEvent<boolean>(NOTIFICATION_SOCKET_PREF_CHANGE_EVENT, { detail: enabled }),
  );
}
